/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
import { Json, RoleFunction } from '@eagle/common';
import { AlertResponse, Alert as AlertType, Geofence, GeofenceType, Person, PersonType, SharedThingType, Thing, ThingType } from '@eagle/core-data-types';
import { checkLocation, FeatureTypes } from '@eagle/data-function-types';
import {
  CacheDataTypes,
  DetailPage,
  DynamicIcon,
  evaluate,
  FetchOne,
  FetchOneOfAll,
  FlexBox,
  FormatAddress,
  FormatTimestamp,
  getEventFeatureV2,
  getEventHint,
  getEventLabelV2,
  getEventLabelWithFlag,
  getEventTranslation,
  getNavigateState,
  GroupsCard,
  Icon,
  INLAY_MAP_DEFAULT_ZOOM,
  InlayMap,
  innerText,
  LabelsEditor,
  LoadingButton,
  MediaCard,
  MediaListPageType,
  MiddleSpinner,
  Portals,
  ResponsiveText,
  StaticData,
  switchToPortal,
  T_MANY,
  TagsEditor,
  TRACK_ALERT_DETAIL_DEFAULT_TEMPLATE,
  Undefinable,
  useApiErrorHandler,
  useAuthenticated,
  useBoolFlag,
  useCustomRoutes,
  useFetchOneCache,
  useHasAuthorization,
  useNumberFlag,
  usePromise,
  useResponsiveText,
  useSmallScreen,
  useUiTemplate,
  validateLocationType
} from '@eagle/react-common';
import { Alert, Breadcrumbs, Button, Chip, Divider, IconButton, Link, Stack, styled, Tooltip, Typography, TypographyProps } from '@ethings/ui-toolkit';
import { Box, Card, CardContent, useTheme } from '@mui/material';
import L from 'leaflet';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useLocation, useNavigate, useParams } from 'react-router-dom';
import { check } from 'validata';
import { buildTripsCoreHistoryRange, HISTORY_CUSTOM_DATE_RANGE_FLAG, resolveTripHistoryMaxRangeHours, TRIPS_CORE_EXPERIENCE_FLAG } from '../history/history-date-range.utils';
import { AlertDetailProps, AlertMapProps, PersonCardProps, ThingCardProps } from './alert-details-types';
import { buildAlertHistoryReferenceData } from './alert-history-navigation';

interface TimeZoneStackProps {
  date: Date;
  label: string;
  tooltipTitle: JSX.Element;
  zoneAbbreviation: string;
  zoneName: string;
}

interface LocationState {
  previousUrl: string;
}

const VIEW_HISTORY_TIME_DIFF = 15;
const MEDIA_PERMISSIONS = [RoleFunction.MEDIA_VIEWER] as const;
const ADMINISTRATOR = [RoleFunction.ADMINISTRATOR] as const;
const GEOFENCE_ADMINISTRATOR = [RoleFunction.GEOFENCE_ADMINISTRATOR] as const;
const ALERT_ADMINISTRATOR = [RoleFunction.ALERT_ADMINISTRATOR] as const;

const alertDetailItemRenderer = (alert: AlertResponse, alertCard: JSX.Element, thingPersonGroupsCard: JSX.Element, thingCard: JSX.Element, personCard: JSX.Element, alertMap: JSX.Element, mediaCard: JSX.Element, template: unknown): JSX.Element => {
  return <>{evaluate(template, { alert, alertCard, thingPersonGroupsCard, thingCard, personCard, alertMap, mediaCard })}</>;
};

const ToggleListButton = styled(Button)(({ theme }) => ({ marginTop: theme.spacing(2), padding: 0 }));

const AlertMap: FC<AlertMapProps> = ({ data }) => {
  const getLocation = (): unknown => data?.location;
  let location;
  try {
    location = check(checkLocation, getLocation);
  }
  catch (e) {
    location = undefined;
  }

  return (
    <FlexBox sx={{ flexGrow: 0, height: '400px' }}>
      <InlayMap
        center={location && L.latLng(location.latitude, location.longitude)}
        data-testid="alert-map-container"
        drawerLayerSelection
        pinLabel={(location?.address && location?.address.length)
          && <Stack spacing={0.5}>
            <FormatAddress value={location?.address} variant="body2" />
          </Stack>
        }
        zoom={INLAY_MAP_DEFAULT_ZOOM}
      />
    </FlexBox>
  );
};

const TimeZoneStack: FC<TimeZoneStackProps> = ({ date, label, tooltipTitle, zoneName, zoneAbbreviation }): JSX.Element => {
  return (
    <Stack data-chromatic="ignore">
      <Stack direction="row" spacing={0.5}>
        <Typography variant="caption">{label}</Typography>
        <Tooltip arrow followCursor title={tooltipTitle}>
          <Typography variant="caption">
            (<Typography style={{ textDecoration: 'underline dotted' }} variant="caption">{zoneAbbreviation}</Typography>)
          </Typography>
        </Tooltip>
      </Stack>
      <FormatTimestamp value={date} zone={zoneName} />
    </Stack>
  );
};

export const AlertCard: FC<{ alert: AlertResponse }> = ({ alert }): JSX.Element => {
  const { handleUpdateError } = useApiErrorHandler();
  const { axios } = useAuthenticated();
  const alertCache = useFetchOneCache(CacheDataTypes.ALERT);
  const { geofence: geofenceRoute } = useCustomRoutes();
  const { hasAuthorization } = useHasAuthorization();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { t, i18n } = useTranslation(['common', 'track']);

  const viewGeofencePermissions = hasAuthorization(GEOFENCE_ADMINISTRATOR) && hasAuthorization(ADMINISTRATOR);
  const localTimeZone = DateTime.fromJSDate(alert.occurred, { zone: DateTime.now().zoneName }).setLocale(i18n.language);
  const location = validateLocationType(() => alert.data?.location);
  const alertZoneName = location?.timeZone?.id ?? '';
  const alertTimeZone = DateTime.fromJSDate(alert.occurred, { zone: alertZoneName }).setLocale(i18n.language);
  const isSameTimeZone = localTimeZone.offsetNameShort === alertTimeZone.offsetNameShort;

  const [alertState, setAlertState] = useState(alert);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const hasEditPermissions = hasAuthorization(ALERT_ADMINISTRATOR);
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const alertDisplay = getEventLabelWithFlag(alert.featureTypeId, alert.alertTypeId, alert.feature, flagUseEventV3);
  const displayTagsAndLabels = useBoolFlag('tracking-display-tags-labels-on-alert-detail');

  const handlePropertyChange = (property: string) => (value: Json): Promise<void> => {
    setSaveDisabled(false);
    setAlertState({ ...alertState, [property]: value });
    return Promise.resolve();
  };

  const updateAlertDetails = async (): Promise<void> => {
    try {
      if (!hasEditPermissions) return;
      setSaveInProgress(true);
      try {
        await axios.patch(`/api/v1/alert/${alert.alertId}`, {
          labels: alertState.labels ?? {},
          tags: alertState.tags ?? [],
        });
      } catch {
        throw new Error(alertDisplay);
      }
      await alertCache.invalidate(alert.alertId);
      enqueueSnackbar(t('common:common.hint.update-success', { entity: alertDisplay }), { variant: 'success' });
    } catch (err) {
      handleUpdateError(err, CacheDataTypes.ALERT, alertDisplay);
    } finally {
      setSaveInProgress(false);
    }
  };

  const renderDateTime = (): JSX.Element => {
    if (!localTimeZone || !alertTimeZone) {
      return (
        <Stack spacing={theme.spacing(2)}>
          <Typography variant="h5">{t('common:component.alert-table.labels.title')}</Typography>
          <Typography>{t('common:component.alert-table.hint.error')}</Typography>;
        </Stack>
      );
    }

    return (
      <Stack spacing={theme.spacing(2)}>
        <Typography variant="h5">{t('common:component.alert-table.labels.title')}</Typography>
        {isSameTimeZone || isEmpty(alert.data)
          ? <Stack direction="row" alignItems="center" justifyContent="space-between">
            <FormatTimestamp value={alert.occurred} />
            <Typography variant="caption">
              <FormatTimestamp format="relative" value={alert.occurred} />
            </Typography>
          </Stack>
          : <Stack spacing={theme.spacing(2)}>
            <TimeZoneStack
              date={alert.occurred}
              label={t('track:page.alert-detail.timezone.labels')}
              tooltipTitle={<>{`${localTimeZone.offsetNameLong} (${localTimeZone.offsetNameShort})`}</>}
              zoneAbbreviation={localTimeZone.offsetNameShort}
              zoneName={localTimeZone.zoneName}
            />
            <Divider />
            <TimeZoneStack
              date={alert.occurred}
              label={t('track:page.alert-detail.local-timezone.labels')}
              tooltipTitle={<>{`${alertTimeZone.offsetNameLong} (${alertTimeZone.offsetNameShort})`}</>}
              zoneAbbreviation={alertTimeZone.offsetNameShort}
              zoneName={alertTimeZone.zoneName}
            />
          </Stack>
        }
      </Stack>
    );
  };

  const renderGeofenceData = (): JSX.Element => {
    if (!alert?.eventSpecificData?.geofence || !alert?.eventSpecificData?.geofenceType) return <></>;
    const geofence = alert.eventSpecificData.geofence as Geofence;
    const geofenceType = alert.eventSpecificData.geofenceType as GeofenceType;
    const geofenceHref = switchToPortal(Portals.TRACK, Portals.ADMIN, `${geofenceRoute}/${geofence._id}`);

    return (
      <Stack spacing={theme.spacing(2)}>
        <Typography variant="h5">{t('track:page.alert-detail.geofence-detail.labels')}</Typography>
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Typography variant="caption">
              {geofenceType.display}
            </Typography>
            <Typography>{geofence.display}</Typography>
          </Stack>
          {geofenceHref && viewGeofencePermissions
            && <Button href={geofenceHref}>{t('common:common.action.view')}</Button>
          }
        </Stack>
      </Stack>
    );
  };

  const renderTagsAndLabels = (): JSX.Element => {
    if (!displayTagsAndLabels) return <></>;
    return (
      <Stack spacing={theme.spacing(2)}>
        <TagsEditor
          data-testid="alert-type-select"
          disabled={!hasEditPermissions || saveInProgress}
          label={t('common:terms.tag')}
          onChange={handlePropertyChange('tags')}
          placeholder={t('common:common.hint.tag')}
          size="small"
          tags={alertState.tags}
        />
        <Typography variant="h6">{t('common:terms.label')}</Typography>
        <LabelsEditor
          data-testid="alert-group-select"
          disabled={!hasEditPermissions || saveInProgress}
          labels={alertState.labels}
          onChange={handlePropertyChange('labels')}
          placeholder={t('common:common.hint.label')}
          size="small"
          textfieldLabel={t('common:terms.label')}
        />
        {hasEditPermissions
          && <LoadingButton
            data-testid="save-button"
            disabled={saveDisabled || saveInProgress}
            loadingCaption={t('common:common.hint.saving')}
            sx={{ alignSelf: 'end', minWidth: 100 }}
            task={updateAlertDetails}
          >
            {t('common:common.action.save')}
          </LoadingButton>
        }
      </Stack>
    );
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={theme.spacing(4)}>
          {renderDateTime()}
          {renderGeofenceData()}
          {renderTagsAndLabels()}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ThingCard: FC<ThingCardProps> = ({ thingIds }) => {
  const { thing: customRoutesThing } = useCustomRoutes();
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation(['track']);

  const h5ThingText = t('terms:thing', { count: thingIds.length });
  const h5ThingStyles = useResponsiveText('1.5rem', innerText(h5ThingText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  return (
    <Card data-testid='thing-card'>
      <CardContent>
        <Typography variant="h5" mb={2} style={{
          fontSize: h5ThingStyles.fontSize,
          lineHeight: h5ThingStyles.lineHeight,
          wordBreak: h5ThingStyles.wordBreak,
          whiteSpace: h5ThingStyles.whiteSpace,
          overflow: h5ThingStyles.overflow,
          textOverflow: h5ThingStyles.textOverflow,
        }}>
          {h5ThingText}
        </Typography>

        {thingIds.map((thingId, i: number) => (i === 0 || isExpanded)
          && <Stack key={i}>
            {i !== 0
              && <Box mt={2} mb={1.5} mx={0} ><Divider /></Box>
            }
            <FetchOne
              id={thingId}
              dataType={CacheDataTypes.THING}
              renderFactory={(thing: Thing) => (
                <FetchOneOfAll
                  id={thing.thingTypeId}
                  dataType={CacheDataTypes.THING_TYPE}
                  renderFactory={(thingType: ThingType) => {
                    const entityLink = { display: t('common:common.action.view'), url: `/${customRoutesThing}/${thing._id}` };

                    if (!thing.sharedThingId) {
                      return (
                        <StaticData
                          data-testid="thing-static-data"
                          data={thing}
                          dataType={thingType}
                          entityLink={entityLink}
                        />
                      );
                    }

                    return (
                      <FetchOneOfAll
                        dataType={CacheDataTypes.SHARED_THING_TYPE}
                        id={thingType.sharedThingTypeId}
                        renderFactory={(sharedThingType: SharedThingType) => (
                          <StaticData
                            data-testid="thing-static-data"
                            data={thing}
                            dataType={thingType}
                            entityLink={entityLink}
                            sharedDataType={sharedThingType}
                          />
                        )}
                      />
                    );
                  }}
                />
              )}
            />
          </Stack>
        )}

        {(thingIds.length > 1)
          && <Typography
            onClick={() => setIsExpanded(!isExpanded)}
            color="primary"
            style={{ cursor: 'pointer' }}
            mt={2}
            variant="subtitle1"
          >
            {isExpanded ? t('common:common.action.hideOthers') : t('common:common.labels.othersWithCount', { count: thingIds.length - 1 })}
          </Typography>
        }
      </CardContent>
    </Card>
  );
};

const PersonCard: FC<PersonCardProps> = ({ personIds }) => {
  const { person: customRoutesPerson } = useCustomRoutes();
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation(['track']);

  const h5PersonText = t('terms:person', { count: personIds.length });
  const h5PersonStyles = useResponsiveText('1.5rem', innerText(h5PersonText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  return (
    <Card data-testid='person-card'>
      <CardContent>
        <Typography mb={2} variant="h5" style={{
          fontSize: h5PersonStyles.fontSize,
          lineHeight: h5PersonStyles.lineHeight,
          wordBreak: h5PersonStyles.wordBreak,
          whiteSpace: h5PersonStyles.whiteSpace,
          overflow: h5PersonStyles.overflow,
          textOverflow: h5PersonStyles.textOverflow,
        }}>
          {h5PersonText}
        </Typography>
        {personIds.map((personId, i: number) => (i === 0 || isExpanded)
          && <Stack key={i}>
            {i !== 0
              && <Box mt={2} mb={1.5} mx={0} ><Divider /></Box>
            }
            <FetchOne
              id={personId}
              dataType={CacheDataTypes.PERSON}
              renderFactory={(person: Person) => (
                <FetchOneOfAll
                  id={person.personTypeId}
                  dataType={CacheDataTypes.PERSON_TYPE}
                  renderFactory={(personType: PersonType) => (
                    <StaticData data-testid='person-static-data' data={person} dataType={personType} entityLink={{ display: t('common:common.action.view'), url: `/${customRoutesPerson}/${person._id}` }} />
                  )}
                />
              )}
            />
          </Stack>
        )}
        {(personIds.length > 1)
          && (() => {
            return (
              <ToggleListButton
                onClick={() => setIsExpanded(!isExpanded)}
                size="small"
              >
                {isExpanded ? t('common:common.action.hideOthers') : t('common:common.labels.othersWithCount', { count: personIds.length - 1 })}
              </ToggleListButton>
            );
          })()
        }
      </CardContent>
    </Card>
  );
};

const ThingPersonGroupsCard: FC<PersonCardProps & ThingCardProps> = ({ personIds, thingIds }) => {
  const { t } = useTranslation(['track']);

  const thingGroupIds = thingIds.flatMap((thingId) => {
    const thingCache = useFetchOneCache(CacheDataTypes.THING);
    const [result] = usePromise<Undefinable<Thing>>(
      () => thingCache.one<Thing>(thingId),
      [thingCache, thingId],
    );
    return result?.groupIds ?? [];
  });

  const personGroupIds = personIds.flatMap((personId) => {
    const personCache = useFetchOneCache(CacheDataTypes.PERSON);
    const [result] = usePromise<Undefinable<Person>>(
      () => personCache.one<Person>(personId),
      [personCache, personId],
    );
    return result?.groupIds ?? [];
  });

  const thingPersonGroupIds = thingGroupIds.concat(personGroupIds);
  const groupIds = [...new Set(thingPersonGroupIds)];

  return <GroupsCard groupIds={groupIds} noGroupsText={t('track:page.alert-detail.no-thing-person-group.hint')} isClickable />;
};

const renderAlertCard = (data: AlertResponse): JSX.Element => <AlertCard alert={data} />;
const renderThingPersonGroupsCard = (data: AlertResponse): JSX.Element => <ThingPersonGroupsCard personIds={data.personIds} thingIds={data.thingIds} />;
const renderThingCard = (data: AlertResponse): JSX.Element => data.thingIds.length !== 0 ? <ThingCard thingIds={data.thingIds} /> : <></>;
const renderPersonCard = (data: AlertResponse): JSX.Element => data.personIds.length > 0 ? <PersonCard personIds={data.personIds} /> : <></>;
const renderAlertMap = (data: AlertResponse): JSX.Element => <AlertMap data={data.data} occurred={data.occurred} />;
const renderMediaCard = (data: AlertResponse, mediaPermissions: boolean): JSX.Element => {
  if (!mediaPermissions) return <></>;

  return (
    <FetchOne
      id={data.thingIds[0]}
      dataType={CacheDataTypes.THING}
      renderFactory={(thing: Thing) => (
        <FetchOneOfAll
          id={thing.thingTypeId}
          dataType={CacheDataTypes.THING_TYPE}
          renderFactory={({ features }: ThingType) => (
            <MediaCard
              data-testid="alert-media-card"
              displayOnCard
              entity={thing}
              entityTypeFeatures={features}
              navigateState={getNavigateState(data.occurred)}
              pageType={MediaListPageType.ALERTS}
              timeZone={validateLocationType(() => data.data.location)?.timeZone?.id}
              url={`/api/v2/alert-media-data/alert/${data.alertId}`}
            />
          )}
        />
      )}
    />
  );
};

export const AlertDetail: FC<AlertDetailProps> = ({ alert = undefined }) => {
  const { t } = useTranslation(['common', 'track']);
  const { alerts: customRoutesAlerts, history: customRoutesHistory } = useCustomRoutes();
  const { alertId } = useParams();
  const { userInfo } = useAuthenticated();
  const theme = useTheme();
  const href = useHref(`/${customRoutesAlerts}`);
  const location = useLocation();
  const navigate = useNavigate();
  const flagThingHistory = useBoolFlag('track-thing-history-feature');
  const flagTripsCoreExperience = useBoolFlag(TRIPS_CORE_EXPERIENCE_FLAG);
  const maxHistoryCustomDateRangeHours = useNumberFlag(HISTORY_CUSTOM_DATE_RANGE_FLAG);
  const flagHintEnabled = useBoolFlag('portals-event-translation-hints-feature');
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const flagUseTemplateAlertDetail = useBoolFlag('track-data-templated-alert-detail-feature-temporary');
  const { hasAuthorization } = useHasAuthorization();
  const alerts = useFetchOneCache(CacheDataTypes.ALERT);
  const { template: template, loaded: templateLoaded } = useUiTemplate('track-alert-detail', TRACK_ALERT_DETAIL_DEFAULT_TEMPLATE);
  const smallScreen = useSmallScreen();

  const mediaPermissions = hasAuthorization(MEDIA_PERMISSIONS);

  const loadData = useCallback(
    () => {
      if (!alert) return alerts.one<AlertResponse & { _id: string }>(alertId);
      return Promise.resolve(alert);
    },
    [alertId, userInfo.accountId]
  );

  const [alertData] = usePromise(() => loadData(), [userInfo.accountId]);

  const renderPageContent = (data: AlertResponse): JSX.Element => {
    const alertCard = renderAlertCard(data);
    const thingPersonGroupsCard = renderThingPersonGroupsCard(data);
    const thingCard = renderThingCard(data);
    const personCard = renderPersonCard(data);
    const alertMap = renderAlertMap(data);
    const mediaCard = renderMediaCard(data, mediaPermissions);

    if (flagUseTemplateAlertDetail) {
      return <>
        {alertDetailItemRenderer(data, alertCard, thingPersonGroupsCard, thingCard, personCard, alertMap, mediaCard, template)}
      </>;
    }
    return <>
      <Stack direction="column" flex={[2, 2]} spacing={2} minWidth={0}>
        {alertCard}
        {thingPersonGroupsCard}
        {thingCard}
        {personCard}
      </Stack>
      <Stack
        direction="column"
        flex={[3, 3]}
        spacing={2}
        minWidth={0}
      >
        {alertMap}
        {mediaCard}
      </Stack>
    </>;
  };

  const viewHistory = (): void => {
    if (!alertData) return;
    const alertTime = DateTime.fromJSDate(alertData.occurred);
    const maxRangeHours = resolveTripHistoryMaxRangeHours(maxHistoryCustomDateRangeHours);
    const tripsCoreRange = buildTripsCoreHistoryRange(alertTime, maxRangeHours);

    navigate(`/${customRoutesHistory}`, {
      state: {
        dateEnd: flagTripsCoreExperience ? tripsCoreRange.dateEnd : alertTime.plus({ minutes: VIEW_HISTORY_TIME_DIFF }).toJSDate(),
        dateStart: flagTripsCoreExperience ? tripsCoreRange.dateStart : alertTime.minus({ minutes: VIEW_HISTORY_TIME_DIFF }).toJSDate(),
        entityId: alertData.thingIds[0],
        entityItemFields: {
          entity: CacheDataTypes.THING,
          entityType: CacheDataTypes.THING_TYPE,
          entityTypeKey: 'thingTypeId',
        },
        filters: [],
        focused: true,
        ...(flagTripsCoreExperience ? { referenceData: buildAlertHistoryReferenceData(alertData) } : {}),
      },
    });
  };

  const actions = flagThingHistory
    ? [
      {
        label: t('track:common.action.view-history'),
        onClick: viewHistory,
        icon: <DynamicIcon icon={<Icon icon="HistoryIcon" />} />,
        testId: 'view-history-button',
      },
    ]
    : [];

  const breadcrumbs = (
    <Breadcrumbs>
      <Link
        color="inherit"
        href={href}
        onClick={(event) => {
          event.preventDefault();
          const state = location.state as LocationState | null;
          if (state?.previousUrl === `/${customRoutesAlerts}`) {
            navigate(-1);
          } else {
            navigate(`/${customRoutesAlerts}`);
          }
        }}
        underline="hover"
      >
        {t('common:terms.alert', { count: T_MANY })}
      </Link>
      <Typography color="text.primary" data-testid="alert-detail-subtitle-breadcrumb">{t('track:page.alert-detail.title')}</Typography>
    </Breadcrumbs>
  );

  const renderPageAlert = (): JSX.Element => {
    if (!alertData?.suppressed) return <></>;
    return (
      <Alert severity="warning" icon={<Icon icon="BellOff" />}>
        {t('common:component.alerts-table.hint.suppression')}
      </Alert>
    );
  };

  const renderPageTitle = ({ alertTypeId, feature, featureTypeId }: AlertType): JSX.Element => {
    const { label, hint, featureInstance, featureLabel } = flagUseEventV3 ? getEventTranslation(feature, alertTypeId) : { label: getEventLabelV2(featureTypeId, alertTypeId), hint: getEventHint(feature, alertTypeId), featureInstance: undefined, featureLabel: getEventFeatureV2(feature) };
    const subtitleSx: TypographyProps = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
    const title = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? featureLabel : label;
    const subtitle = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? label : featureLabel;
    return <>
      <Stack direction="row" spacing={theme.spacing(1)}>
        <ResponsiveText
          variant="h4"
          responsiveOptions={{
            minFontSize: smallScreen ? '1.25rem' : '1.5rem',
            maxFontSize: '2.125rem',
            textLengthThreshold: smallScreen ? 1 : 15,
          }}
          style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
        >
          {title}
        </ResponsiveText>
        {flagHintEnabled && hint &&
          <Tooltip data-testid="event-hint" title={hint} arrow>
            <IconButton size="small" aria-label="info">
              <Icon icon="InfoIcon" fontSize="small" color="action" />
            </IconButton>
          </Tooltip>}
      </Stack>
      <span style={{ display: 'none' }}> - </span>
      <Stack direction="row" spacing={theme.spacing(1)}>
        <Typography variant="body2" {...subtitleSx}>{subtitle}</Typography>
        {featureInstance && <Chip label={featureInstance} variant="outlined" size="small" />}
      </Stack>
    </>;
  };

  if (flagUseTemplateAlertDetail) {
    if (!templateLoaded) return <MiddleSpinner />;
  }

  return (
    <Box sx={{
      display: 'flex',
      flex: [1, 1],
      flexDirection: 'column',
      minWidth: 0,
    }}>
      <DetailPage<AlertResponse>
        data-testid='alert-detail'
        actions={actions}
        breadcrumbs={breadcrumbs}
        loadData={loadData}
        pageAlert={renderPageAlert()}
        pageIcon={<Icon icon="AlertIcon" />}
        renderDisplay={({ alertTypeId, featureTypeId, feature }) => getEventLabelWithFlag(featureTypeId, alertTypeId, feature, flagUseEventV3)}
        renderPageContent={renderPageContent}
        renderPageTitle={renderPageTitle}
      />
    </Box>
  );
};
